import { useState, useEffect, useContext, useMemo } from "react";
import { useAuth } from "../hooks/useAuth";
import { LanguageContext } from "../hooks/LanguageContext";
import { createApiWrapper } from "../services/api/apiWrapper";
import { useSnackbar } from '../contexts/SnackbarContext';
import { useErrorTracking } from '../utils/errorTracking';
import { useMixPanel } from '../hooks/useMixPanel';
import { PublicationStatus, PublishStatus } from "../constants";
import { getCountryInfo } from "../utils/countryCodeConverter";
import { CmsApiResponse, CmsDetailApiResponse, CmsItem } from "types/api/news";
import { sanitizeGoogleSheetsArtifacts } from '../common/newsHtmlUtils';

export interface ProcessedNewsArticle extends CmsItem {
  id: string;
  title: string;
  content: string;
  imageUrl?: string;
  lastEditedAt: string;
}

interface SnackbarContextType {
  showSnackbar: (message: string, severity?: 'info' | 'success' | 'warning' | 'error') => void;
}

interface LanguageContextType {
  currentLanguage: string;
}

const useNews = (articleId?: string, countryAlias?: string) => {
  const { REACT_APP_COSMIC_URL } = process.env;
  const { userInfo, logout } = useAuth() as unknown as {
    userInfo: {
      token: string;
      countryCode: string;
      isAffiliate: boolean;
      [key: string]: unknown;
    };
    logout: () => void;
  };
  const { showSnackbar } = useSnackbar() as SnackbarContextType;
  const { trackError } = useErrorTracking();
  const { logEvent } = useMixPanel();
  const { currentLanguage } = useContext(LanguageContext) as LanguageContextType;

  const api = useMemo(() => createApiWrapper({
    token: userInfo.token,
    logout,
    showSnackbar,
    trackError,
    userInfo,
  }), [userInfo, logout, showSnackbar, trackError]);

  const selectedLanguage = useMemo(() => currentLanguage.split("-")[0], [currentLanguage]);
  // The market whose news we fetch: an explicit `countryAlias` (from the
  // country selector, GO-3110) when provided, otherwise the user's home market.
  const homeMarket = useMemo(() => getCountryInfo(userInfo.countryCode)?.alpha3, [userInfo.countryCode]);
  const userMarket = useMemo(() => countryAlias ?? homeMarket, [countryAlias, homeMarket]);
  const accountType = useMemo(() => (userInfo.isAffiliate ? "Affiliate" : "Distributor"), [userInfo.isAffiliate]);

  const publicationStatus = process.env.NODE_ENV === 'development'
    ? PublicationStatus.QA_STAGING
    : PublicationStatus.PUBLISHED;

  const [news, setNews] = useState<ProcessedNewsArticle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchNews = async () => {
      const startTime = Date.now();
      const requestId = `news_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      try {
        setLoading(true);

        // Track news fetch initiation
        logEvent('news_fetch_started', {
          articleId: articleId || null,
          requestType: articleId ? 'single_article' : 'news_list',
          selectedLanguage,
          userMarket,
          requestId,
          publicationStatus,
        });

        const url = articleId
          ? `${REACT_APP_COSMIC_URL}/office/newsContent/public/detail/${articleId}`
          : `${REACT_APP_COSMIC_URL}/office/newsContent/public/list`;

        // Create parallel requests for both selected language and English
        const cosmicNewsSelectedLangPromise = api.get(url, {
          addPrefix: false,
          addToken: false,
          addXApplicationHeader: false,
          params: {
            countryAlias: userMarket,
            languageCode: selectedLanguage,
            accountType,
            publicationStatus: publicationStatus,
            publishStatus: PublishStatus.ACTIVE
          }
        });

        const cosmicNewsEnglishPromise = selectedLanguage !== 'en'
          ? api.get(url, {
            addPrefix: false,
            addToken: false,
            addXApplicationHeader: false,
            params: {
              countryAlias: userMarket,
              languageCode: 'en',
              accountType,
              publicationStatus: publicationStatus,
              publishStatus: PublishStatus.ACTIVE
            }
          })
          : Promise.resolve({ data: articleId ? { item: null } : { items: [] } });

        const [cosmicSelectedLangResult, cosmicEnglishResult] = await Promise.allSettled([
          cosmicNewsSelectedLangPromise,
          cosmicNewsEnglishPromise,
        ]);

        const apiResponseTime = Date.now() - startTime;

        // Track API response timing and any failures
        const failedSources: string[] = [];
        if (cosmicSelectedLangResult.status === 'rejected') failedSources.push('cosmic_selected_lang');
        if (cosmicEnglishResult.status === 'rejected') failedSources.push('cosmic_english');

        logEvent('news_api_responses_received', {
          requestId,
          responseTime: apiResponseTime,
          selectedLanguage,
          userMarket,
          failedSources: failedSources.length > 0 ? failedSources.join(', ') : 'none',
          successfulSources: 2 - failedSources.length,
        });

        // Extract data from successful responses with fallbacks
        const cosmicSelectedLangResponse = cosmicSelectedLangResult.status === 'fulfilled' ? cosmicSelectedLangResult.value : null;
        const cosmicEnglishResponse = cosmicEnglishResult.status === 'fulfilled' ? cosmicEnglishResult.value : null;

        // Extract data from responses
        const selectedLangData: CmsItem[] = cosmicSelectedLangResponse
          ? (articleId
            ? cosmicSelectedLangResponse.data?.item ? [(cosmicSelectedLangResponse.data as CmsDetailApiResponse).item] : []
            : (cosmicSelectedLangResponse.data as CmsApiResponse)?.items || [])
          : [];

        const englishData: CmsItem[] = cosmicEnglishResponse
          ? (articleId
            ? cosmicEnglishResponse.data?.item ? [(cosmicEnglishResponse.data as CmsDetailApiResponse).item] : []
            : (cosmicEnglishResponse.data as CmsApiResponse)?.items || [])
          : [];

        // Merge newsContent from both responses: combine articles and their content
        const mergedCosmicData: CmsItem[] = [];

        // Create a map of English articles by _id for quick lookup
        const englishArticlesMap = new Map<string, CmsItem>();
        englishData.forEach(item => {
          englishArticlesMap.set(item._id, item);
        });

        // Process selected language articles first
        selectedLangData.forEach(selectedItem => {
          const englishItem = englishArticlesMap.get(selectedItem._id);
          const mergedItem: CmsItem = {
            ...selectedItem,
            newsContent: [
              ...selectedItem.newsContent,
              ...(englishItem?.newsContent || [])
            ]
          };
          mergedCosmicData.push(mergedItem);
          // Remove from English map to avoid duplicates
          englishArticlesMap.delete(selectedItem._id);
        });

        // Add remaining English-only articles
        englishArticlesMap.forEach(englishItem => {
          mergedCosmicData.push(englishItem);
        });

        // Track language fallback usage
        let fallbackCount = 0;
        let translatedCount = 0;
        let emptyContentCount = 0;

        const processedCosmicNews: ProcessedNewsArticle[] = mergedCosmicData.map(item => {
          const selectedLangContent = item.newsContent.find(c => c.languageCode === selectedLanguage);
          const englishContent = item.newsContent.find(c => c.languageCode === 'en');
          const fallbackContent = item.newsContent[0];

          const content = selectedLangContent || englishContent || fallbackContent;

          // Track content language usage
          if (selectedLangContent) {
            translatedCount++;
          } else if (englishContent) {
            fallbackCount++;
          } else if (!content) {
            emptyContentCount++;
          }

          // Sanitize HTML content to remove Google Sheets artifacts
          // This is a temporary fix until CMS applies proper content validation
          const rawContent = content?.content || '';
          const sanitizedContent = sanitizeGoogleSheetsArtifacts(rawContent);

          return {
            ...item,
            id: item._id,
            title: content?.title || 'Untitled',
            content: sanitizedContent,
            imageUrl: content?.media?.imageUrls[0]?.url,
            lastEditedAt: item.lastEditedAt,
          };
        });

        processedCosmicNews.sort((a, b) => new Date(b.lastEditedAt).getTime() - new Date(a.lastEditedAt).getTime());

        const totalTime = Date.now() - startTime;

        // Track successful news fetch with detailed metrics
        logEvent('news_fetch_success', {
          requestId,
          totalTime,
          selectedLanguage,
          userMarket,
          totalArticles: processedCosmicNews.length,
          cmsArticles: processedCosmicNews.length,
          translatedArticles: translatedCount,
          fallbackArticles: fallbackCount,
          emptyContentArticles: emptyContentCount,
          fallbackUsageRate: processedCosmicNews.length > 0 ? (fallbackCount / processedCosmicNews.length) * 100 : 0,
          requestType: articleId ? 'single_article' : 'news_list',
          hasLanguageFallback: fallbackCount > 0,
        });

        setNews(processedCosmicNews);
      } catch (err) {
        const totalTime = Date.now() - startTime;

        // Track news fetch errors with context
        logEvent('news_fetch_error', {
          requestId,
          totalTime,
          selectedLanguage,
          userMarket,
          errorType: err instanceof Error ? err.constructor.name : 'UnknownError',
          errorMessage: err instanceof Error ? err.message : 'Unknown error occurred',
          requestType: articleId ? 'single_article' : 'news_list',
          publicationStatus,
        });

        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    if (userMarket) {
      fetchNews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMarket, selectedLanguage, articleId, publicationStatus, accountType]);

  return { news, loading, error };
};

export { useNews };
